import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetHomepageSettingsQuery,
  useGetServicesQuery,
} from "../redux/services/api";
import { setSettings } from "../redux/slices/homepageSlice";
import { selectSettings } from "../redux/slices/homepageSlice";
import { useTranslation } from "../hooks/useTranslation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
} from "@fortawesome/free-solid-svg-icons";

const Footer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const settings = useSelector(selectSettings);
  const { t, isArabic } = useTranslation();

  // Fetch data with RTK Query
  const { data: settingsData } = useGetHomepageSettingsQuery();
  const { data: servicesData, isLoading: loading } = useGetServicesQuery();

  // Load settings into Redux
  useEffect(() => {
    if (settingsData && settingsData.success) {
      dispatch(setSettings(settingsData.settings));
    }
  }, [settingsData, dispatch]);

  // Get active services
  const services = servicesData?.services
    ? servicesData.services.filter((service) => service.is_active).slice(0, 5)
    : [
        { id: 1, title: "Cleaning and Maintaining" },
        { id: 2, title: "Air Conditioning" },
        { id: 3, title: "Water Cooling Systems" },
        { id: 4, title: "Maintenance Services" },
        { id: 5, title: "Technical Support" },
      ];

  const handleServiceClick = (serviceId) => {
    navigate(`/service/${serviceId}`);
  };

  // Inject footer tags
  useEffect(() => {
    const footerTags = settings?.headTags?.footerTags || "";
    if (footerTags) {
      // Create a container for footer tags if it doesn't exist
      let footerTagsContainer = document.getElementById("footer-tags-container");
      if (!footerTagsContainer) {
        footerTagsContainer = document.createElement("div");
        footerTagsContainer.id = "footer-tags-container";
        footerTagsContainer.style.display = "none"; // Hide the container
        document.body.appendChild(footerTagsContainer);
      }

      // Parse and inject footer tags
      footerTagsContainer.innerHTML = footerTags;

      // Move all elements from container to footer (or body if no footer)
      const footerElement = document.querySelector("footer");
      const targetElement = footerElement || document.body;

      // Get all script and link elements
      const scripts = footerTagsContainer.querySelectorAll("script");
      const links = footerTagsContainer.querySelectorAll("link");
      const otherElements = Array.from(footerTagsContainer.children).filter(
        (el) => el.tagName.toLowerCase() !== "script" && el.tagName.toLowerCase() !== "link"
      );

      // Handle scripts separately (they need to be recreated to execute)
      scripts.forEach((script) => {
        const src = script.getAttribute("src");
        if (src) {
          // Check if script already exists
          const existingScript = document.querySelector(`script[src="${src}"]`);
          if (!existingScript) {
            const newScript = document.createElement("script");
            newScript.src = src;
            if (script.type) newScript.type = script.type;
            if (script.async) newScript.async = true;
            if (script.defer) newScript.defer = true;
            targetElement.appendChild(newScript);
          }
        } else if (script.textContent) {
          // Inline script
          const newScript = document.createElement("script");
          newScript.textContent = script.textContent;
          if (script.type) newScript.type = script.type;
          targetElement.appendChild(newScript);
        }
      });

      // Handle links
      links.forEach((link) => {
        const rel = link.getAttribute("rel");
        const href = link.getAttribute("href");
        if (rel && href) {
          const existingLink = document.querySelector(`link[rel="${rel}"][href="${href}"]`);
          if (!existingLink) {
            targetElement.appendChild(link.cloneNode(true));
          }
        }
      });

      // Handle other elements
      otherElements.forEach((element) => {
        targetElement.appendChild(element.cloneNode(true));
      });

      // Cleanup function
      return () => {
        // Remove injected elements when component unmounts or tags change
        scripts.forEach((script) => {
          const src = script.getAttribute("src");
          if (src) {
            const scriptToRemove = document.querySelector(`script[src="${src}"]`);
            if (scriptToRemove) scriptToRemove.remove();
          }
        });
        links.forEach((link) => {
          const rel = link.getAttribute("rel");
          const href = link.getAttribute("href");
          if (rel && href) {
            const linkToRemove = document.querySelector(`link[rel="${rel}"][href="${href}"]`);
            if (linkToRemove) linkToRemove.remove();
          }
        });
      };
    }
  }, [settings?.headTags?.footerTags]);

  return (
    <footer className="footer" id="contact">
      <div className="container">
        <div className="footer-content">
          <div className="footer-section">
            <div className="logo">
              {settings?.branding?.logo ? (
                <img
                  src={settings.branding.logo}
                  alt={
                    isArabic && settings.branding.companyNameAr
                      ? settings.branding.companyNameAr
                      : settings.branding.companyName || "Company Logo"
                  }
                  className="footer-logo-image"
                />
              ) : (
                <>
                  <div className="logo-icon"></div>
                  <span className="logo-text">SILVER FORT</span>
                </>
              )}
            </div>
            <p
              style={{
                color: "#ccc",
                marginTop: "15px",
                maxWidth: "100%",
              }}
            >
              {isArabic && settings?.footer?.paragraphAr
                ? settings.footer.paragraphAr
                : settings?.footer?.paragraph}
            </p>
          </div>

          <div className="footer-section">
            <h3>{t("footer.services")}</h3>
            <ul>
              {loading ? (
                <li>{t("common.loading")}</li>
              ) : services.length > 0 ? (
                services.map((service) => (
                  <li key={service.id}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleServiceClick(service.id);
                      }}
                    >
                      •{" "}
                      {isArabic && service.title_ar
                        ? service.title_ar
                        : service.title}
                    </a>
                  </li>
                ))
              ) : (
                <li>{t("services.noServices")}</li>
              )}
            </ul>
          </div>

          <div className="footer-section">
            <h3>{t("contact.title")}</h3>
            <div className="footer-contact-list">
              <div className="footer-contact-item">
                <div className="footer-contact-icon">
                  <FontAwesomeIcon icon={faEnvelope} />
                </div>
                <a
                  href={`mailto:${
                    settings?.contact?.email || "Email@gmail.com"
                  }`}
                  className="footer-contact-text"
                  style={{
                    textDecoration: "none",
                    color: "inherit",
                  }}
                >
                  {settings?.contact?.email || "Email@gmail.com"}
                </a>
              </div>
              <div className="footer-contact-item">
                <div className="footer-contact-icon">
                  <FontAwesomeIcon icon={faPhone} />
                </div>
                <a
                  href={`tel:${settings?.contact?.phone || "+971999999999"}`}
                  className="footer-contact-text"
                  style={{
                    textDecoration: "none",
                    color: "inherit",
                  }}
                >
                  {settings?.contact?.phone || "+971999999999"}
                </a>
              </div>
              <div className="footer-contact-item">
                <div className="footer-contact-icon">
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                </div>
                <span className="footer-contact-text">
                  {isArabic && settings?.contact?.addressAr
                    ? settings.contact.addressAr
                    : settings?.contact?.address || "location: Ajman"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
